import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { CheckHeader } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useDataTable } from 'Src/feature/data-table/useDataTable';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { useOrderType } from 'Src/feature/order-type/useOrderType';
import { useRevenueCenters } from 'Src/feature/revenue-center/useRevenueCenter';
import { useToast } from 'Src/feature/toasts/useToast';
import { SearchComparator as Comp } from 'Src/models/queryOpts';
import useRoute from 'Src/routes/useRoute';

import { CheckMergeModalProps } from './CheckMergeModal';

const sectionId = 'useCheckMergeModal';

const useCheckMergeModal = (props: CheckMergeModalProps) => {
  const { t } = useTranslation();
  const { crumbs, navigate, route } = useRoute();
  const toast = useToast();
  const log = useLogger().getSubLogger({ name: sectionId });

  // query
  const currentCheck = useCurrentCheck().query;
  const checkHeader = useCheckHeader.query.collection();
  const revenueCenters = useRevenueCenters.query.getAll();
  const orderTypes = useOrderType.query.list();
  const clockedInEmployees = useEmployee.query.clockedInList();
  const employeeGetAll = useEmployee.query.getAll();
  // mutate
  const mergeCheck = useCheck.mutate.mergeCheck();

  const [selectedTargetCheck, setSelectedTargetCheck] = useState<CheckHeader | undefined>(undefined);

  // make an employee list which consists of all clocked in employees and any employee who created a check
  const employees = Array.from(clockedInEmployees.data ?? []);
  checkHeader.data?.all.forEach((ch) => {
    if (employees.findIndex((emp) => emp.id === ch.employeeId) >= 0) return;
    const emp = employeeGetAll.data?.find((emp) => emp.id === ch.employeeId);
    if (emp) employees.push(emp);
  });

  const table = useDataTable({
    defaultPerPage: 100000,
    defaultSortOrderBy: 'timeOpen',
    data: (checkHeader.data?.all || [])
      .filter((ch) => ch.status === 'Opened')
      .filter((ch) => currentCheck.data?.id !== ch.id)
      .map((ch) => ({
        ...ch,
        timeOpen: Math.abs(DateTime.fromMillis(ch.openedAt ?? 0).diffNow().toMillis()),
        revCenter: revenueCenters.data?.find((rc) => rc.id === ch.revenueCenterId)?.name,
        orderType: (orderTypes.data || []).find((ot) => ot.id === ch.orderTypeId)?.name,
      })),
  });

  table.store.filter.createMany([
    { field: 'revenueCenterId', value: 'all', operator: Comp.in },
    { field: 'orderTypeId', value: 'all', operator: Comp.in },
    { field: 'employeeId', value: 'all', operator: Comp.in },
  ]);

  const currentValues = {
    revenueCenterId: (table.store.filter.find('revenueCenterId')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
    orderTypeId: (table.store.filter.find('orderTypeId')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
    employeeId: (table.store.filter.find('employeeId')?.value as string || '').split(',').filter((val) => val !== '').map((val) => Number.isNaN(Number(val)) ? val : parseInt(val)),
  };

  table.store.filter.updateMany([
    { field: 'revenueCenterId', isDisabled: currentValues.revenueCenterId.includes('all') },
    { field: 'orderTypeId', isDisabled: currentValues.orderTypeId.includes('all') },
    { field: 'employeeId', isDisabled: currentValues.employeeId.includes('all') },
  ]);

  const updateFilter = (field: keyof typeof currentValues, newValues: (string | number)[]) => {
    const prevValues = (table.store.filter.find(field)?.value as string || '')
      .split(',')
      .filter((val) => val !== '')
      .map((val) => Number.isNaN(Number(val)) ? val : parseInt(val));
    const allPrevSelected = prevValues.includes('all');
    const allCurrentlySelected = newValues.includes('all');
    const allToggled = (allPrevSelected && !allCurrentlySelected) || (!allPrevSelected && allCurrentlySelected);
    if (newValues.length === 0) newValues = ['all'];
    else if (allToggled) newValues = ['all'];
    else {
      const allIdx = newValues.findIndex((val) => val === 'all');
      if (allIdx >= 0) newValues.splice(allIdx, 1);
    }
    table.store.filter.updateOne(field, { value: newValues.join(',') });
  };

  const transferBarItems = [{
    primary: t('check.labels.checkNumber'),
    secondary: <Copy optional value={currentCheck.data?.number}/>
  }, {
    primary: t('labels.table'),
    secondary: <Copy optional value={currentCheck.data?.tableName}/>
  }, {
    primary: t('labels.owner'),
    secondary: <Copy optional value={currentCheck.data?.employeeShortName}/>
  },
  <FontIcon name="arrow_forward" key="icon"/>,
  ];
  if (selectedTargetCheck) {
    transferBarItems.push({
      primary: t('check.labels.checkNumber'),
      secondary: <Copy optional value={selectedTargetCheck.number}/>,
    }, {
      primary: t('labels.table'),
      secondary: <Copy optional value={selectedTargetCheck.tableName}/>,
    }, {
      primary: t('labels.owner'),
      secondary: <Copy optional value={selectedTargetCheck.employeeShortName}/>,
    });
  } else {
    transferBarItems.push(
      <div/>, // The `<div/>`s here are to make the spacing even
      <Copy value={t('checkMerge.labels.noTableSelected')} italic color="textTertiary"/>,
      <div/>, // The `<div/>`s here are to make the spacing even
    );
  }

  const isLoading = currentCheck.isLoading || checkHeader.isLoading || revenueCenters.isLoading || clockedInEmployees.isLoading || employeeGetAll.isLoading;

  return {
    isLoading,
    currentValues,
    options: {
      revenueCenterId: [{
        value: 'all',
        label: t('revenueCenter.labels.all'),
        selected: currentValues.revenueCenterId.includes('all'),
      },
      ...(Array.from(revenueCenters.data || []).sort((a, b) => a.id - b.id).map(({ id, name }) => ({ value: id, label: name }))),
      ],
      orderTypeId: [{
        value: 'all',
        label: t('orderType.all'),
        selected: currentValues.orderTypeId.includes('all'),
      },
      ...(orderTypes.data || []).sort((a, b) => a.name.localeCompare(b.name)).map(({ id, name }) => ({ value: id as number, label: name })),
      ],
      employeeId: [{
        value: 'all',
        label: t('employee.labels.all'),
        selected: currentValues.employeeId.includes('all'),
      },
      ...(employees || [])
        .sort((a, b) => {
          const a_fullName = a.firstName + a.lastName;
          const b_fullName = b.firstName + b.lastName;
          return a_fullName.localeCompare(b_fullName);
        })
        .map((employee) =>
          ({ value: employee.id, label: t('labels.util.employeeFullName', { firstName: employee.firstName, lastName: employee.lastName.charAt(0) }) })),
      ],
    },
    filterHandlers: {
      revenueCenterId: (newValues: (string | number)[]) => updateFilter('revenueCenterId', newValues),
      orderTypeId: (newValues: (string | number)[]) => updateFilter('orderTypeId', newValues),
      employeeId: (newValues: (string | number)[]) => updateFilter('employeeId', newValues),
    },
    table,
    selectedTargetCheck,
    setSelectedTargetCheck,
    transferBarItems,
    handleMerge: () => {
      if (typeof selectedTargetCheck?.id !== 'number') return log.debug('handleMerge()', '!selectedTargetCheck?.id', { selectedTargetCheck });
      if (typeof currentCheck.data?.id !== 'number') return log.debug('handleMerge()', '!currentCheck.data?.id', { currentCheck });

      mergeCheck.mutate({
        checkId: selectedTargetCheck.id,
        checkIds: [currentCheck.data.id],
      }, {
        onSuccess: () => {
          props.close();
          navigate(crumbs.pos.check.main.interpolate({
            checkId: selectedTargetCheck?.id,
            itemSelect: route.params.value.one,
          }), {
            skipCheckExitPrompt: true,
          });
        },
        onError: (err) => {
          toast.show.serviceError(err, { id: `${sectionId}.handleMerge` });
        }
      });
    },
  };
};

export { useCheckMergeModal };
